import { Box, Button, Paper } from '@mui/material'
import React, { useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import * as XLSX from 'xlsx-js-style'
import { getStoredCompanyDetails, getCompanyDisplayFromDetails, getShortCompanyName } from '../../services/companyService'

function IncomeTaxReportGenerated() {
    const CombinedTableRef = useRef(null);
    const location = useLocation();
    const companyDetails = useSelector((state) => state.auth.activeCompanyDetails) || getStoredCompanyDetails();
    const { companyName, addressLine1, addressLine2 } = getCompanyDisplayFromDetails(companyDetails || {});
    const empDetails = location.state?.empDetails || [];
    const year = location.state?.year || new Date().getFullYear();

    const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const startYear = Number(year);
    const monthsList = [];
    for (let m = 3; m < 12; m++) monthsList.push({ title: monthNames[m], year: startYear });
    for (let m = 0; m < 3; m++) monthsList.push({ title: monthNames[m], year: startYear + 1 });
    
    // Get the first month for the filename (April of startYear)
    const firstMonth = monthNames[3];

    const formatNumber = (num) => {
        const roundedNum = Math.round(Number(parseFloat(num) || 0));
        return roundedNum.toLocaleString('en-US');
    };

    const getRawValue = (num) => {
        return Number(parseFloat(num) || 0);
    };

    const calculateMonthTotal = (monthTitle, key) => {
        return empDetails.reduce((sum, row) => {
            const cell = row[monthTitle];
            const val = cell ? (key === 'gross' ? (cell.gross || 0) : (cell.income_tax ?? cell.tax ?? 0)) : 0;
            return sum + getRawValue(val);
        }, 0);
    };

    const handleGenerateExcel = () => {
        const wb = XLSX.utils.book_new();
        const totalColsFull = 4 + (monthsList.length * 2) + 2;
        const isZeroOrNull = (v) => v == null || v === '' || (typeof v === 'number' && !isNaN(v) && Number(v) === 0);

        const headerRow1Full = ['Sr.no', 'Employee Code', 'Employee Name', 'PAN'];
        monthsList.forEach(mn => {
            headerRow1Full.push(`${mn.title} ${mn.year}`);
            headerRow1Full.push('');
        });
        headerRow1Full.push('TOTAL');
        headerRow1Full.push('');
        const headerRow2Full = ['', '', '', ''];
        monthsList.forEach(() => { headerRow2Full.push('GROSS'); headerRow2Full.push('TDS'); });
        headerRow2Full.push('GROSS');
        headerRow2Full.push('TDS');

        const fullDataRows = [];
        empDetails.forEach((row, i) => {
            const dataRow = [i + 1, row.employee_code || '', row.name || '', row.pan_card_no || ''];
            let rowTotalGross = 0, rowTotalTax = 0;
            monthsList.forEach(mk => {
                const cell = row[mk.title];
                const gross = cell && cell.gross !== undefined ? getRawValue(cell.gross) : 0;
                const tax = cell && (cell.income_tax !== undefined ? getRawValue(cell.income_tax) : getRawValue(cell.tax ?? 0)) || 0;
                rowTotalGross += gross;
                rowTotalTax += tax;
                dataRow.push(gross);
                dataRow.push(tax);
            });
            dataRow.push(rowTotalGross);
            dataRow.push(rowTotalTax);
            fullDataRows.push(dataRow);
        });

        const visibleColIndices = [0, 1, 2, 3];
        for (let c = 4; c < totalColsFull; c++) {
            const hasNonZero = fullDataRows.some(row => !isZeroOrNull(row[c]));
            if (hasNonZero) visibleColIndices.push(c);
        }
        const totalCols = visibleColIndices.length;
        const pick = (row) => visibleColIndices.map(c => row[c]);

        const wsData = [];
        wsData.push([companyName || '', ...Array(totalCols - 1).fill('')]);
        wsData.push([addressLine1 || '', ...Array(totalCols - 1).fill('')]);
        wsData.push([addressLine2 || '', ...Array(totalCols - 1).fill('')]);
        wsData.push([`Income Tax details for ${firstMonth} ${startYear}`, ...Array(totalCols - 1).fill('')]);
        wsData.push(pick(headerRow1Full));
        wsData.push(pick(headerRow2Full));
        fullDataRows.forEach(row => wsData.push(pick(row)));

        if (empDetails.length > 0) {
            const totalRowFull = ['', 'GRAND TOTAL', '', ''];
            monthsList.forEach(mn => {
                totalRowFull.push(calculateMonthTotal(mn.title, 'gross'));
                totalRowFull.push(calculateMonthTotal(mn.title, 'income_tax'));
            });
            totalRowFull.push(monthsList.reduce((s, mn) => s + calculateMonthTotal(mn.title, 'gross'), 0));
            totalRowFull.push(monthsList.reduce((s, mn) => s + calculateMonthTotal(mn.title, 'income_tax'), 0));
            wsData.push(pick(totalRowFull));
        }

        const ws = XLSX.utils.aoa_to_sheet(wsData);

        // Calculate column widths dynamically (very tight fitting)
        const calculateColumnWidths = (data) => {
            const colWidths = [];
            const numCols = data[0]?.length || 0;

            for (let col = 0; col < numCols; col++) {
                let maxWidth = 0;
                let hasRealData = false;

                data.forEach((row, rowIndex) => {
                    // Skip header rows (rows 0-4)
                    if (rowIndex < 4) return;
                    
                    const cellValue = row[col];
                    
                    // Check if this is real data (not empty, null, undefined)
                    // We DO want to show zeros, so don't skip them
                    if (cellValue === undefined || cellValue === null || cellValue === '') {
                        return;
                    }
                    
                    hasRealData = true;
                    let displayValue = String(cellValue);
                    
                    // Account for number formatting with commas for numeric columns
                    if (col >= 4 && rowIndex >= 6) {
                        const numValue = Number(cellValue);
                        if (!isNaN(numValue)) {
                            displayValue = numValue.toLocaleString('en-IN');
                        }
                    }
                    
                    maxWidth = Math.max(maxWidth, displayValue.length);
                });

                // For columns with only headers or no real data, use header width
                // For data columns, add minimal padding
                if (!hasRealData && maxWidth === 0) {
                    // Check header text for this column (rows 4-5)
                    const headerText = String(data[4]?.[col] || data[5]?.[col] || '');
                    colWidths.push({ wch: Math.max(headerText.length + 1, 6) });
                } else {
                    colWidths.push({ wch: maxWidth + 1 });
                }
            }

            return colWidths;
        };

        ws['!cols'] = calculateColumnWidths(wsData);

        // Merge cells for company header rows
        ws['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } }, // Company name
            { s: { r: 1, c: 0 }, e: { r: 1, c: totalCols - 1 } }, // Address line 1
            { s: { r: 2, c: 0 }, e: { r: 2, c: totalCols - 1 } }, // Address line 2
            { s: { r: 3, c: 0 }, e: { r: 3, c: totalCols - 1 } }  // Title row
        ];

        // Month/TOTAL merges skipped when columns are filtered (variable layout)

        // Common border style
        const borderStyle = {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
        };

        // Style for company name header
        const companyNameStyle = {
            font: { bold: true, sz: 16, color: { rgb: "000000" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: borderStyle
        };

        // Style for column headers (lighter blue)
        const headerStyle = {
            font: { bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "5B9BD5" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: borderStyle
        };

        // Style for data cells
        const dataStyle = {
            border: borderStyle,
            alignment: { vertical: "center" }
        };

        // Apply style to company header cells (rows 1-4)
        for (let row = 0; row <= 3; row++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
            if (ws[cellAddress]) {
                ws[cellAddress].s = companyNameStyle;
            }
            
            // Remove empty cells in merged range (to prevent visible lines)
            for (let col = 1; col < totalCols; col++) {
                const emptyCellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[emptyCellAddress]) {
                    delete ws[emptyCellAddress];
                }
            }
        }

        // Apply styles to column headers (rows 5-6)
        for (let row = 4; row <= 5; row++) {
            for (let col = 0; col < totalCols; col++) {
                const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[cellAddress]) {
                    ws[cellAddress].s = headerStyle;
                }
            }
        }

        // Apply styles to data cells (starting from row 7)
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let row = 6; row <= range.e.r; row++) {
            for (let col = 0; col < totalCols; col++) {
                const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[cellAddress]) {
                    const isGrandTotal = row === range.e.r;
                    const isNumeric = col >= 4;
                    
                    ws[cellAddress].s = {
                        ...dataStyle,
                        font: { bold: isGrandTotal },
                        alignment: {
                            ...dataStyle.alignment,
                            horizontal: isNumeric ? "right" : (col === 0 ? "center" : "left")
                        }
                    };

                    // Format numbers with Indian comma style
                    if (isNumeric && ws[cellAddress].v !== '' && ws[cellAddress].v !== 0) {
                        ws[cellAddress].z = '#,##0';
                    }
                }
            }
        }

        XLSX.utils.book_append_sheet(wb, ws, 'TDS');
        XLSX.writeFile(wb, `${getShortCompanyName(companyName) || 'Report'} Report for Income Tax ${firstMonth.substring(0, 3)} ${startYear}.xlsx`);
    };

    const columnWidths = {
        srNo: '60px',
        empCode: '120px',
        empName: '220px',
        panNumber: '130px',
        monthGross: '120px',
        monthTax: '100px'
    };

    // add two final columns for TOTAL GROSS and TOTAL TDS
    columnWidths.totalGross = '140px';
    columnWidths.totalTax = '120px';

    // Fixed column widths (first 4 columns) for sticky positioning
    const fixedColumnWidths = [60, 120, 220, 130];
    // UI: narrower widths for unfixed (month/total) columns to reduce spacing
    const uiMonthGross = 95;
    const uiMonthTax = 80;
    const uiTotalGross = 120;
    const uiTotalTax = 100;
    const totalWidth = fixedColumnWidths.reduce((a, b) => a + b, 0) + (monthsList.length * (uiMonthGross + uiMonthTax)) + uiTotalGross + uiTotalTax;
    const getLeftOffset = (colIndex) => fixedColumnWidths.slice(0, colIndex).reduce((a, b) => a + b, 0);

    return (
        <Box sx={{ display: "flex", justifyContent: "center", padding: "10px", overflowX: "hidden", maxWidth: "100vw" }}>
            <Box sx={{ width: "100%", maxWidth: "100%", padding: '20px', minWidth: 0, overflow: "hidden" }}>
                <Button variant='contained' color='success' onClick={handleGenerateExcel} sx={{ marginBottom: 2 }}>
                    Download TDS Report (excel)
                </Button>

                <div style={{ 
                    height: '600px', 
                    width: '100%', 
                    maxWidth: '100%', 
                    overflow: 'auto', 
                    border: '1px solid #dee2e6', 
                    borderRadius: '4px', 
                    backgroundColor: '#fff' 
                }}>
                    <table
                        ref={CombinedTableRef}
                        style={{
                            display: 'table',
                            width: '100%',
                            minWidth: `${totalWidth}px`,
                            tableLayout: 'fixed',
                            borderCollapse: 'collapse',
                            margin: 0
                        }}
                        className="table"
                    >
                        <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#212529', color: 'white', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.2)' }}>
                            <tr>
                                <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '1px solid #dee2e6', width: fixedColumnWidths[0], minWidth: fixedColumnWidths[0], position: 'sticky', left: getLeftOffset(0), zIndex: 11, background: '#212529', borderRight: '2px solid #dee2e6' }}>Sr.no</th>
                                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #dee2e6', width: fixedColumnWidths[1], minWidth: fixedColumnWidths[1], position: 'sticky', left: getLeftOffset(1), zIndex: 11, background: '#212529', borderRight: '2px solid #dee2e6' }}>Employee Code</th>
                                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #dee2e6', width: fixedColumnWidths[2], minWidth: fixedColumnWidths[2], position: 'sticky', left: getLeftOffset(2), zIndex: 11, background: '#212529', borderRight: '2px solid #dee2e6' }}>Employee Name</th>
                                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #dee2e6', width: fixedColumnWidths[3], minWidth: fixedColumnWidths[3], position: 'sticky', left: getLeftOffset(3), zIndex: 11, background: '#212529', borderRight: '2px solid #dee2e6' }}>PAN</th>
                                {monthsList.map((mn, idx) => (
                                    <th key={idx} style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }} colSpan={2}>{mn.title} {mn.year}</th>
                                ))}
                                <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }} colSpan={2}>TOTAL</th>
                            </tr>
                            <tr>
                                <th style={{ padding: '12px 8px', borderBottom: '1px solid #dee2e6', width: fixedColumnWidths[0], minWidth: fixedColumnWidths[0], position: 'sticky', left: getLeftOffset(0), zIndex: 11, background: '#212529', borderRight: '2px solid #dee2e6' }}></th>
                                <th style={{ padding: '12px 8px', borderBottom: '1px solid #dee2e6', width: fixedColumnWidths[1], minWidth: fixedColumnWidths[1], position: 'sticky', left: getLeftOffset(1), zIndex: 11, background: '#212529', borderRight: '2px solid #dee2e6' }}></th>
                                <th style={{ padding: '12px 8px', borderBottom: '1px solid #dee2e6', width: fixedColumnWidths[2], minWidth: fixedColumnWidths[2], position: 'sticky', left: getLeftOffset(2), zIndex: 11, background: '#212529', borderRight: '2px solid #dee2e6' }}></th>
                                <th style={{ padding: '12px 8px', borderBottom: '1px solid #dee2e6', width: fixedColumnWidths[3], minWidth: fixedColumnWidths[3], position: 'sticky', left: getLeftOffset(3), zIndex: 11, background: '#212529', borderRight: '2px solid #dee2e6' }}></th>
                                {monthsList.map((mn, idx) => (
                                    <React.Fragment key={idx}>
                                        <th style={{ padding: '12px 8px', textAlign: 'right', borderBottom: '1px solid #dee2e6' }}>GROSS</th>
                                        <th style={{ padding: '12px 8px', textAlign: 'right', borderBottom: '1px solid #dee2e6' }}>TDS</th>
                                    </React.Fragment>
                                ))}
                                <th style={{ padding: '12px 8px', textAlign: 'right', borderBottom: '1px solid #dee2e6' }}>GROSS</th>
                                <th style={{ padding: '12px 8px', textAlign: 'right', borderBottom: '1px solid #dee2e6' }}>TDS</th>
                            </tr>
                        </thead>
                        <tbody>
                        {empDetails && empDetails.length > 0 ? empDetails.map((row, i) => (
                                <tr key={i} style={{ backgroundColor: 'white' }}>
                                    <td style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '1px solid #dee2e6', width: fixedColumnWidths[0], minWidth: fixedColumnWidths[0], position: 'sticky', left: getLeftOffset(0), zIndex: 5, background: 'white', borderRight: '2px solid #dee2e6' }}>{i + 1}</td>
                                    <td style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6', width: fixedColumnWidths[1], minWidth: fixedColumnWidths[1], position: 'sticky', left: getLeftOffset(1), zIndex: 5, background: 'white', borderRight: '2px solid #dee2e6' }}>{row.employee_code || ''}</td>
                                    <td style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6', width: fixedColumnWidths[2], minWidth: fixedColumnWidths[2], position: 'sticky', left: getLeftOffset(2), zIndex: 5, background: 'white', borderRight: '2px solid #dee2e6' }}>{row.name || ''}</td>
                                    <td style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6', width: fixedColumnWidths[3], minWidth: fixedColumnWidths[3], position: 'sticky', left: getLeftOffset(3), zIndex: 5, background: 'white', borderRight: '2px solid #dee2e6' }}>{row.pan_card_no || ''}</td>
                                    {monthsList.map((mk, mi) => {
                                        const cell = row[mk.title];
                                        const gross = cell && cell.gross !== undefined ? cell.gross : '';
                                        const tax = cell && (cell.income_tax !== undefined ? cell.income_tax : cell.tax ?? '');
                                        return (
                                            <React.Fragment key={mi}>
                                                <td style={{ padding: '12px 8px', borderBottom: '1px solid #dee2e6', textAlign: 'right' }}>{gross !== '' ? formatNumber(gross) : ''}</td>
                                                <td style={{ padding: '12px 8px', borderBottom: '1px solid #dee2e6', textAlign: 'right' }}>{tax !== '' ? formatNumber(tax) : ''}</td>
                                            </React.Fragment>
                                        )
                                    })}
                                    {/* Row totals */}
                                    {(() => {
                                        const rowTotalGross = monthsList.reduce((s, mk) => {
                                            const c = row[mk.title];
                                            return s + getRawValue(c ? (c.gross || 0) : 0);
                                        }, 0);
                                        const rowTotalTax = monthsList.reduce((s, mk) => {
                                            const c = row[mk.title];
                                            const t = c ? (c.income_tax !== undefined ? c.income_tax : c.tax ?? 0) : 0;
                                            return s + getRawValue(t);
                                        }, 0);
                                        return (
                                            <>
                                                <td style={{ padding: '12px 8px', borderBottom: '1px solid #dee2e6', textAlign: 'right' }}>{formatNumber(rowTotalGross)}</td>
                                                <td style={{ padding: '12px 8px', borderBottom: '1px solid #dee2e6', textAlign: 'right' }}>{formatNumber(rowTotalTax)}</td>
                                            </>
                                        )
                                    })()}
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={4 + 12 * 2 + 2} style={{ textAlign: 'center', padding: '20px', position: 'sticky', left: 0, zIndex: 5, background: 'white' }}>No data to display</td>
                                </tr>
                            )}

                            {empDetails.length > 0 && (
                                <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                                    <td style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '1px solid #dee2e6', width: fixedColumnWidths[0], minWidth: fixedColumnWidths[0], position: 'sticky', left: getLeftOffset(0), zIndex: 5, background: '#f8f9fa', borderRight: '2px solid #dee2e6' }}></td>
                                    <td style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6', width: fixedColumnWidths[1], minWidth: fixedColumnWidths[1], position: 'sticky', left: getLeftOffset(1), zIndex: 5, background: '#f8f9fa', borderRight: '2px solid #dee2e6' }}>GRAND TOTAL</td>
                                    <td style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6', width: fixedColumnWidths[2], minWidth: fixedColumnWidths[2], position: 'sticky', left: getLeftOffset(2), zIndex: 5, background: '#f8f9fa', borderRight: '2px solid #dee2e6' }}></td>
                                    <td style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6', width: fixedColumnWidths[3], minWidth: fixedColumnWidths[3], position: 'sticky', left: getLeftOffset(3), zIndex: 5, background: '#f8f9fa', borderRight: '2px solid #dee2e6' }}></td>
                                    {monthsList.map((mn, idx) => {
                                        const totalGross = calculateMonthTotal(mn.title, 'gross');
                                        const totalTax = calculateMonthTotal(mn.title, 'income_tax');
                                        return (
                                            <React.Fragment key={idx}>
                                                <td style={{ padding: '12px 8px', borderBottom: '1px solid #dee2e6', textAlign: 'right' }}>{formatNumber(totalGross)}</td>
                                                <td style={{ padding: '12px 8px', borderBottom: '1px solid #dee2e6', textAlign: 'right' }}>{formatNumber(totalTax)}</td>
                                            </React.Fragment>
                                        )
                                    })}
                                    {(() => {
                                        const grandTotalGross = monthsList.reduce((s, mn) => s + calculateMonthTotal(mn.title, 'gross'), 0);
                                        const grandTotalTax = monthsList.reduce((s, mn) => s + calculateMonthTotal(mn.title, 'income_tax'), 0);
                                        return (
                                            <>
                                                <td style={{ padding: '12px 8px', borderBottom: '1px solid #dee2e6', textAlign: 'right' }}>{formatNumber(grandTotalGross)}</td>
                                                <td style={{ padding: '12px 8px', borderBottom: '1px solid #dee2e6', textAlign: 'right' }}>{formatNumber(grandTotalTax)}</td>
                                            </>
                                        )
                                    })()}
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Box>
        </Box>
    )
}

export default IncomeTaxReportGenerated


